import { useState, useEffect } from 'react'
import { DollarSign, TrendingUp, ChevronDown, ChevronUp, Search, X } from 'lucide-react'
import { dataRoomService } from '@/services/dataRoomService'
import { projectService } from '@/services/projectService'
import { Project, ProjectSearchResult } from '@/types/project'
import { Modal } from './Modal'
import { Button, Input } from '@/components/atoms'
import { Form } from '@/components/molecules'
import { parseNumberInput } from '@/utils/format'

export interface ContactUsModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmitted: () => void
}

export const ContactUsModal = ({ isOpen, onClose, onSubmitted }: ContactUsModalProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<ProjectSearchResult[]>([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [loadingProject, setLoadingProject] = useState(false)
  const [formData, setFormData] = useState({
    tranche_size: '',
    valuation: '',
    type: 'BUY' as 'BUY' | 'SELL'
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [showFullDescription, setShowFullDescription] = useState(false)

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setSearchResults([])
      setShowSearchResults(false)
      setSelectedProject(null)
      setFormData({ tranche_size: '', valuation: '', type: 'BUY' })
      setError('')
      setSuccess(false)
      setShowFullDescription(false)
    }
  }, [isOpen])

  // Search for projects
  const searchProjects = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      setShowSearchResults(false)
      return
    }
    
    try {
      setSearchLoading(true)
      const results = await projectService.searchProjects(query)
      setSearchResults(results)
      setShowSearchResults(true)
    } catch (err) {
      console.error('Error searching projects:', err)
    } finally {
      setSearchLoading(false)
    }
  }

  // Debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchQuery.trim() && !selectedProject) {
        searchProjects(searchQuery)
      } else {
        setSearchResults([])
        setShowSearchResults(false)
      }
    }, 300)
    
    return () => {
      clearTimeout(handler)
    }
  }, [searchQuery, selectedProject])

  // Handle project selection
  const handleSelectProject = async (project: ProjectSearchResult) => {
    if (!project.project_id) return
    
    try {
      setLoadingProject(true)
      setError('')
      // Fetch full project details
      const fullProject = await dataRoomService.getProjectDetail(project.project_id, true)
      setSelectedProject(fullProject)
      setSearchQuery(fullProject.project_name)
      setShowSearchResults(false)
    } catch (err) {
      console.error('Error fetching project details:', err)
      setError('Failed to load project details. Please try again.')
    } finally {
      setLoadingProject(false)
    }
  }

  // Handle clearing selected project
  const handleClearProject = () => {
    setSelectedProject(null)
    setSearchQuery('')
    setShowSearchResults(false)
    setFormData({ tranche_size: '', valuation: '', type: 'BUY' })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedProject) {
      setError('Please select a project first.')
      return
    }

    setLoading(true)
    setError('')
    setSuccess(false)

    try {
      // Parse the input values (handles formats like 100k, 1m, 1B, 100,000, etc.)
      const parsedTrancheSize = parseNumberInput(formData.tranche_size);
      const parsedValuation = formData.valuation ? parseNumberInput(formData.valuation) : undefined;

      if (isNaN(parsedTrancheSize) || parsedTrancheSize <= 0) {
        setError('Please enter a valid amount for tranche size.');
        return;
      }

      if (parsedValuation !== undefined && (isNaN(parsedValuation) || parsedValuation <= 0)) {
        setError('Please enter a valid valuation or leave it empty.');
        return;
      }

      // Create RFQ (same as ProjectRFQModal)
      const rfqData = {
        project_id: selectedProject.project_id,
        tranche_size: parsedTrancheSize,
        type: formData.type,
        ...(parsedValuation !== undefined ? { valuation: parsedValuation } : {})
      }

      await dataRoomService.createRFQ(rfqData)
      setSuccess(true)
      onSubmitted()
      
      // Close modal after 2 seconds to show success message
      setTimeout(() => {
        onClose()
        setSuccess(false)
        setFormData({ tranche_size: '', valuation: '', type: 'BUY' })
        handleClearProject()
      }, 2000)
    } catch (err) {
      setError('Failed to submit inquiry. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const truncateDescription = (text: string) => {
    if (!text) return ''
    const maxChars = 80
    
    if (text.length <= maxChars) {
      return text
    }
    
    const truncated = text.substring(0, maxChars)
    const lastSpaceIndex = truncated.lastIndexOf(' ')
    
    if (lastSpaceIndex > 0) {
      return truncated.substring(0, lastSpaceIndex) + '...'
    }
    
    return truncated + '...'
  }

  const getProjectDescription = () => {
    const description = selectedProject?.description || 'Investment Opportunity'
    if (showFullDescription) {
      return description
    }
    return truncateDescription(description)
  }

  const shouldShowReadMore = () => {
    const description = selectedProject?.description || ''
    return description.length > 80
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title="Contact Us"
      contentClassName="max-w-lg"
      className="bg-black/60 backdrop-blur-md"
    >
      {success ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <TrendingUp size={32} className="text-green-500" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Inquiry Submitted Successfully!</h3>
          <p className="text-muted-foreground">
            Your inquiry has been submitted. Our team will review and get back to you.
          </p>
        </div>
      ) : (
        <>
          {/* Project Search */}
          {!selectedProject ? (
            <div className="mb-6">
              <Form.Field label="Search for a Project" required>
                <div className="relative">
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Type to search for a project..."
                    leftElement={<Search size={16} />}
                    className="w-full"
                  />
                  {searchQuery && (
                    <button
                      onClick={handleClearProject}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                    >
                      <X size={16} className="text-muted-foreground" />
                    </button>
                  )}
                </div>
                
                {/* Search Results */}
                {showSearchResults && (
                  <div className="mt-2 border border-border rounded-lg bg-background max-h-60 overflow-y-auto">
                    {searchLoading ? (
                      <div className="p-4 text-center text-muted-foreground">
                        Searching...
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div className="divide-y divide-border">
                        {searchResults.map((project) => (
                          <button
                            key={project.project_id || project.name}
                            onClick={() => handleSelectProject(project)}
                            className="w-full p-3 hover:bg-muted/50 transition-colors text-left flex items-center gap-3"
                          >
                            {project.logo && (
                              <img 
                                src={project.logo} 
                                alt={project.project_name || project.name || 'Project'} 
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                onError={(e) => {
                                  e.currentTarget.src = '/placeholder-logo.png'
                                }}
                              />
                            )}
                            <span className="font-medium text-foreground">
                              {project.project_name || project.name || 'Unknown Project'}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : searchQuery.trim() ? (
                      <div className="p-4 text-center text-muted-foreground">
                        No projects found
                      </div>
                    ) : null}
                  </div>
                )}
              </Form.Field>
            </div>
          ) : (
            <>
              {/* Project Info */}
              <div className="mb-6 p-4 bg-muted/50 border border-border rounded-lg">
                <div className="flex items-start gap-3 mb-3">
                  {selectedProject.logo && (
                    <img 
                      src={selectedProject.logo} 
                      alt={selectedProject.project_name} 
                      className="w-10 h-10 rounded-full flex-shrink-0 object-cover"
                      onError={(e) => {
                        e.currentTarget.src = '/placeholder-logo.png'
                      }}
                    />
                  )}
                  <div className="flex-1 min-w-0 pr-2">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-foreground">{selectedProject.project_name}</h3>
                      <button
                        onClick={handleClearProject}
                        className="p-1 hover:bg-muted rounded"
                        title="Change project"
                      >
                        <X size={16} className="text-muted-foreground" />
                      </button>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {getProjectDescription()}
                      {shouldShowReadMore() && (
                        <button
                          onClick={() => setShowFullDescription(!showFullDescription)}
                          className="ml-2 text-primary hover:text-primary/80 font-medium inline-flex items-center gap-1"
                        >
                          {showFullDescription ? (
                            <>
                              Read less <ChevronUp size={14} />
                            </>
                          ) : (
                            <>
                              Read more <ChevronDown size={14} />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Funding Rounds:</span>
                    <div className="font-medium text-foreground">{selectedProject.rounds?.length || 0} rounds</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Latest Round:</span>
                    <div className="font-medium text-foreground">
                      {selectedProject.rounds && selectedProject.rounds.length > 0
                        ? selectedProject.rounds.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]?.round_name || 'N/A'
                        : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              <Form onSubmit={handleSubmit}>
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 text-red-500 px-4 py-3 rounded-lg mb-4">
                    {error}
                  </div>
                )}

                <Form.Field label="Request Type" required>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-foreground"
                    required
                  >
                    <option value="BUY">Buy - I want to invest</option>
                    <option value="SELL">Sell - I want to sell my position</option>
                  </select>
                </Form.Field>

                <Form.Field label="Amount / Tranche Size ($)" required>
                  <Input
                    type="text"
                    name="tranche_size"
                    value={formData.tranche_size}
                    onChange={handleChange}
                    placeholder="e.g., 100000, 100k, 1m, 1B"
                    leftElement={<DollarSign size={16} />}
                    required
                  />
                </Form.Field>

                <Form.Field label="Desired Valuation ($)">
                  <Input
                    type="text"
                    name="valuation"
                    value={formData.valuation}
                    onChange={handleChange}
                    placeholder="e.g., 100000, 100k, 1m, 1B (optional)"
                    leftElement={<TrendingUp size={16} />}
                  />
                </Form.Field>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading || loadingProject} className="flex-1">
                    {loading ? 'Submitting...' : 'Submit Inquiry'}
                  </Button>
                </div>
              </Form>
            </>
          )}
        </>
      )}
    </Modal>
  )
}

export default ContactUsModal

